import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../services/supabase';
import { MessageCircle, CheckCircle, Clock, Send, Paperclip } from 'lucide-react';

type Ticket = {
    id: string;
    name: string;
    email: string;
    plan: string;
    category: string;
    status: string;
    created_at: string;
};

type Message = {
    id: string;
    ticket_id: string;
    sender_type: 'user' | 'admin';
    content: string;
    attachment_url: string | null;
    created_at: string;
};

export const AdminSupport: React.FC = () => {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('all');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTickets();

        const ticketsChannel = supabase
            .channel('admin_tickets')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'support_tickets' }, () => {
                fetchTickets();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(ticketsChannel);
        };
    }, []);

    useEffect(() => {
        if (activeTicket) {
            fetchMessages(activeTicket.id);

            const messagesChannel = supabase
                .channel(`admin_messages_${activeTicket.id}`)
                .on(
                    'postgres_changes',
                    { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `ticket_id=eq.${activeTicket.id}` },
                    (payload) => {
                        setMessages(prev => {
                            if (prev.find(m => m.id === payload.new.id)) return prev;
                            return [...prev, payload.new as Message];
                        });
                        scrollToBottom();
                    }
                )
                .subscribe();

            return () => {
                supabase.removeChannel(messagesChannel);
            };
        }
    }, [activeTicket]);

    const fetchTickets = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('support_tickets')
            .select('*')
            .order('created_at', { ascending: false });

        if (data) setTickets(data);
        setLoading(false);
    };

    const fetchMessages = async (ticketId: string) => {
        const { data } = await supabase
            .from('support_messages')
            .select('*')
            .eq('ticket_id', ticketId)
            .order('created_at', { ascending: true });

        if (data) {
            setMessages(data);
            scrollToBottom();
        }
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((!newMessage.trim() && !isUploading) || !activeTicket) return;

        const messageData = {
            ticket_id: activeTicket.id,
            sender_type: 'admin',
            content: newMessage.trim(),
        };

        setNewMessage('');
        await supabase.from('support_messages').insert(messageData);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !activeTicket) return;

        setIsUploading(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `${activeTicket.id}/${crypto.randomUUID()}.${fileExt}`;

        const { data: uploadData } = await supabase.storage
            .from('support-attachments')
            .upload(fileName, file);

        if (uploadData) {
            const { data: { publicUrl } } = supabase.storage
                .from('support-attachments')
                .getPublicUrl(fileName);

            await supabase.from('support_messages').insert({
                ticket_id: activeTicket.id,
                sender_type: 'admin',
                content: '📎 Action related attachment',
                attachment_url: publicUrl
            });
        }
        setIsUploading(false);
    };

    const toggleTicketStatus = async () => {
        if (!activeTicket) return;
        const newStatus = activeTicket.status === 'open' ? 'closed' : 'open';

        await supabase
            .from('support_tickets')
            .update({ status: newStatus })
            .eq('id', activeTicket.id);

        setActiveTicket({ ...activeTicket, status: newStatus });
    };

    const filteredTickets = tickets.filter(t => statusFilter === 'all' || t.status === statusFilter);

    return (
        <div className="h-[calc(100vh-100px)] flex gap-6 animate-in fade-in duration-500">
            {/* Sidebar: Tickets List */}
            <div className="w-1/3 min-w-[300px] flex flex-col bg-finance-card border border-gray-800 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                    <h2 className="text-lg font-bold text-white mb-4">Support Tickets</h2>
                    <div className="flex gap-2 p-1 bg-finance-dark rounded-lg">
                        <button
                            onClick={() => setStatusFilter('all')}
                            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${statusFilter === 'all' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setStatusFilter('open')}
                            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${statusFilter === 'open' ? 'bg-emerald-500/10 text-emerald-400' : 'text-gray-500 hover:text-emerald-400'}`}
                        >
                            Open
                        </button>
                        <button
                            onClick={() => setStatusFilter('closed')}
                            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${statusFilter === 'closed' ? 'bg-gray-800 text-gray-400' : 'text-gray-500 hover:text-gray-400'}`}
                        >
                            Closed
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
                    {loading ? (
                        <div className="text-center py-10 text-gray-500 text-sm">Loading tickets...</div>
                    ) : filteredTickets.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 text-sm">No tickets found.</div>
                    ) : (
                        filteredTickets.map(t => (
                            <button
                                key={t.id}
                                onClick={() => setActiveTicket(t)}
                                className={`w-full text-left p-4 rounded-xl border transition-all ${activeTicket?.id === t.id ? 'bg-finance-darker border-finance-accent' : 'bg-[#0b0f19] border-gray-800 hover:border-gray-700'}`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <span className="text-sm font-bold text-white">{t.name}</span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${t.status === 'open' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-800 text-gray-400'}`}>
                                        {t.status}
                                    </span>
                                </div>
                                <p className="text-xs text-finance-accent font-medium mb-2">{t.category}</p>
                                <div className="flex justify-between items-center text-[11px] text-gray-500">
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(t.created_at).toLocaleDateString()}</span>
                                    <span className="px-1.5 py-0.5 bg-gray-800 rounded">{t.plan}</span>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>

            {/* Main Area: Chat View */}
            <div className="flex-1 flex flex-col bg-finance-card border border-gray-800 rounded-xl overflow-hidden">
                {activeTicket ? (
                    <>
                        {/* Chat Header */}
                        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-finance-darker/50">
                            <div>
                                <h3 className="text-lg font-bold text-white">{activeTicket.name}</h3>
                                <p className="text-xs text-gray-400 mt-1">{activeTicket.email} • {activeTicket.category}</p>
                            </div>
                            <button
                                onClick={toggleTicketStatus}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTicket.status === 'open' ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'}`}
                            >
                                {activeTicket.status === 'open' ? <><CheckCircle className="w-4 h-4" /> Close Ticket</> : <><MessageCircle className="w-4 h-4" /> Reopen Ticket</>}
                            </button>
                        </div>

                        {/* Chat Messages */}
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar flex flex-col space-y-4">
                            {messages.length === 0 ? (
                                <div className="flex items-center justify-center h-full text-gray-500 text-sm">No messages yet.</div>
                            ) : (
                                messages.map(msg => (
                                    <div key={msg.id} className={`flex flex-col max-w-[70%] ${msg.sender_type === 'admin' ? 'self-end' : 'self-start'}`}>
                                        <div className={`p-3 rounded-2xl ${msg.sender_type === 'admin' ? 'bg-finance-accent text-finance-darker rounded-br-none' : 'bg-gray-800 text-gray-100 rounded-bl-none'}`}>
                                            {msg.content && <p className="text-sm font-medium">{msg.content}</p>}
                                            {msg.attachment_url && (
                                                <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" className="block mt-2">
                                                    <img src={msg.attachment_url} alt="Attachment" className="max-w-full rounded-lg border border-black/10" />
                                                </a>
                                            )}
                                        </div>
                                        <span className={`text-[10px] text-gray-500 mt-1 ${msg.sender_type === 'admin' ? 'text-right' : 'text-left'}`}>
                                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Chat Input */}
                        <div className="p-4 border-t border-gray-800 bg-[#0b0f19]">
                            <form onSubmit={handleSendMessage} className="flex items-end gap-3">
                                <label className="p-3 text-gray-400 bg-gray-800 hover:text-white rounded-xl cursor-pointer transition-colors shrink-0">
                                    <Paperclip className="w-5 h-5" />
                                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={isUploading} accept="image/*,.pdf,.doc,.docx" />
                                </label>
                                <div className="flex-1 relative">
                                    <input
                                        type="text"
                                        value={newMessage}
                                        onChange={e => setNewMessage(e.target.value)}
                                        placeholder={activeTicket.status === 'closed' ? 'Ticket closed. Reopen to chat.' : 'Type your reply...'}
                                        disabled={activeTicket.status === 'closed'}
                                        className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl pl-4 pr-12 py-3.5 focus:border-finance-accent outline-none text-sm placeholder:text-gray-500 disabled:opacity-50"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!newMessage.trim() || activeTicket.status === 'closed'}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-finance-accent text-finance-darker rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:hover:bg-finance-accent"
                                    >
                                        <Send className="w-4 h-4" />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                        <MessageCircle className="w-12 h-12 mb-4 opacity-50" />
                        <p>Select a ticket to view the conversation</p>
                    </div>
                )}
            </div>
        </div>
    );
};
